import { useCalculateUserRisk } from "#/services/kyan/hooks/useCalculateUserRisk";
import { RFQLeg, RFQResponseState } from "#/services/kyan/kyan.types";
import { useAccount } from "#/store/wallet/account/account";
import { type CompactAccountState } from "#/types/accounts";
import { type Market } from "#/types/markets";
import { getMmrImr } from "#/utils/getMmrImr";
import { isEqualAddress } from "#/utils/isEqualAddress";
import { isBuy } from "#/utils/marketActionConverter";

const transformRFQLegsToRiskInputs = (legs: RFQLeg[], isMaker: boolean) => {
  return legs.map((leg) => {
    return {
      size: leg.size.toString(),
      instrument: leg.instrumentName,
      limitPrice: leg.price.toString(),
      isBuy: isMaker ? !isBuy(leg.direction) : isBuy(leg.direction),
      isEnabled: true,
    };
  });
};

export const useRfqRiskData = ({
  response,
  portfolioState,
  market,
}: {
  response: RFQResponseState;
  portfolioState: CompactAccountState;
  market: Market;
}) => {
  const { account } = useAccount();
  const isMaker = isEqualAddress(account, response.maker);
  const inputs = transformRFQLegsToRiskInputs(response.legs, isMaker);

  const { im, mm, greeks, isLoading } = useCalculateUserRisk({
    market,
    inputs,
    option: null,
  });

  const { imr, mmr } = getMmrImr({
    equity: portfolioState.equity,
    im,
    mm,
  });

  return {
    imr: { from: portfolioState.imr, to: imr },
    mmr: { from: portfolioState.mmr, to: mmr },
    delta: {
      from: portfolioState.greeks.delta,
      to: greeks.delta,
    },
    gamma: {
      from: portfolioState.greeks.gamma,
      to: greeks.gamma,
    },
    vega: {
      from: portfolioState.greeks.vega,
      to: greeks.vega,
    },
    theta: {
      from: portfolioState.greeks.theta,
      to: greeks.theta,
    },
    im,
    mm,
    greeks,
    isLoading,
  };
};
